import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, CalendarDays, MapPin, Loader2, X, Search, Trash2, ChevronLeft, ChevronRight, List } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

function fmtRange(a, b) {
  if (!a) return 'Dates TBD';
  const opt = { month: 'short', day: 'numeric' };
  try {
    const sa = new Date(a + 'T00:00:00').toLocaleDateString('en-US', opt);
    if (!b || b === a) return sa;
    const sb = new Date(b + 'T00:00:00').toLocaleDateString('en-US', { ...opt, year: 'numeric' });
    return `${sa} – ${sb}`;
  } catch {
    return a;
  }
}

function countdown(start) {
  if (!start) return null;
  const days = Math.ceil((new Date(start + 'T00:00:00') - new Date()) / 86400000);
  if (days > 1) return `${days} days away`;
  if (days === 1) return 'Tomorrow';
  if (days === 0) return 'Today!';
  return null;
}

// All calendar maths is done on plain YYYY-MM-DD strings. Constructing Date
// objects from a bare date string parses as UTC and then renders in local time,
// which shifts a con onto the wrong day for anyone west of Greenwich.
function monthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function shiftMonth(ym, delta) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthGrid(ym) {
  const [y, m] = ym.split('-').map(Number);
  const firstWeekday = new Date(y, m - 1, 1).getDay();
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${ym}-${String(d).padStart(2, '0')}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

// Expand each con across every day it actually runs, so a three-day con dots
// all three days rather than just its start.
function eventsByDay(events, ym) {
  const map = {};
  for (const e of events) {
    const start = e.start_date;
    const end = e.end_date || e.start_date;
    if (!start) continue;
    let cur = start;
    let guard = 0;
    while (cur <= end && guard < 400) {
      if (cur.startsWith(ym)) (map[cur] = map[cur] || []).push(e);
      const [yy, mm, dd] = cur.split('-').map(Number);
      const nxt = new Date(yy, mm - 1, dd + 1);
      cur = `${nxt.getFullYear()}-${String(nxt.getMonth() + 1).padStart(2, '0')}-${String(nxt.getDate()).padStart(2, '0')}`;
      guard++;
    }
  }
  return map;
}

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const INPUT_BASE = 'win-input px-3 py-2 text-sm';
const INPUT = `w-full ${INPUT_BASE}`;
const BTN_PRIMARY = 'flex items-center gap-2 px-4 py-2 win-btn win-btn-default text-sm font-semibold disabled:cursor-not-allowed';
const DIR_PAGE_SIZE = 20;

export default function ConsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cons, setCons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', venue: '', city: '', start_date: '', end_date: '' });
  const [busy, setBusy] = useState(false);

  const [dirQuery, setDirQuery] = useState('');
  const [dirResults, setDirResults] = useState([]);
  const [dirTotal, setDirTotal] = useState(0);
  const [dirOffset, setDirOffset] = useState(0);
  const [dirState, setDirState] = useState('');
  const [dirStates, setDirStates] = useState([]);
  const [dirLoading, setDirLoading] = useState(false);
  const [dirError, setDirError] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const debounceRef = useRef(null);

  // Calendar view
  const [dirView, setDirView] = useState('list');
  const [calMonth, setCalMonth] = useState(() => todayLocal().slice(0, 7));
  const [calEvents, setCalEvents] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [calError, setCalError] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const [confirmDel, setConfirmDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/cons');
      setCons(res.data);
    } catch {
      toast.error('Could not load your conventions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeModal = () => {
    setShowAdd(false);
    setDirQuery('');
    setDirResults([]);
    setDirState('');
    setDirOffset(0);
    setDirTotal(0);
    setDirError(false);
    setDirView('list');
    setCalMonth(todayLocal().slice(0, 7));
    setCalEvents([]);
    setSelectedDay(null);
    setManualMode(false);
    setForm({ name: '', venue: '', city: '', start_date: '', end_date: '' });
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const res = await api.post('/cons', form);
      closeModal();
      navigate(`/cons/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirmDel) return;
    setDelBusy(true);
    try {
      const res = await api.delete(`/cons/${confirmDel.id}`);
      setCons((list) => list.filter((c) => c.id !== confirmDel.id));
      toast.success(res.data?.deleted ? 'Convention deleted' : 'Removed from your conventions');
      setConfirmDel(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    } finally {
      setDelBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // The directory is browsable now, not search-only: an empty query lists the
  // soonest upcoming cons rather than showing nothing until you type a name you
  // already know.
  const fetchDirectory = useCallback(async ({ q = '', state = '', offset = 0, append = false }) => {
    setDirLoading(true);
    setDirError(false);
    try {
      const res = await api.get('/cons/directory', {
        params: { q: q.trim(), state, limit: DIR_PAGE_SIZE, offset },
      });
      const entries = res.data?.entries || [];
      setDirResults((prev) => (append ? [...prev, ...entries] : entries));
      setDirTotal(res.data?.total ?? entries.length);
      setDirOffset(offset);
    } catch {
      if (!append) setDirResults([]);
      setDirError(true);
    } finally {
      setDirLoading(false);
    }
  }, []);

  // Prime the browse list (and the state filter) as soon as the modal opens.
  useEffect(() => {
    if (!showAdd) return;
    fetchDirectory({});
    api.get('/cons/directory/states')
      .then((res) => setDirStates(res.data || []))
      .catch(() => setDirStates([]));
  }, [showAdd, fetchDirectory]);

  const onDirQueryChange = (val) => {
    setDirQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchDirectory({ q: val, state: dirState });
    }, 400);
  };

  const onDirStateChange = (val) => {
    setDirState(val);
    fetchDirectory({ q: dirQuery, state: val });
  };

  const loadMoreDirectory = () =>
    fetchDirectory({
      q: dirQuery,
      state: dirState,
      offset: dirOffset + DIR_PAGE_SIZE,
      append: true,
    });

  const fetchCalendar = useCallback(async (month, state = '') => {
    setCalLoading(true);
    setCalError(false);
    try {
      const res = await api.get('/cons/directory/calendar', { params: { month, state } });
      setCalEvents(res.data?.events || []);
    } catch {
      setCalEvents([]);
      setCalError(true);
    } finally {
      setCalLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!showAdd || dirView !== 'calendar') return;
    setSelectedDay(null);
    fetchCalendar(calMonth, dirState);
  }, [showAdd, dirView, calMonth, dirState, fetchCalendar]);

  const dayMap = useMemo(() => eventsByDay(calEvents, calMonth), [calEvents, calMonth]);

  const pickResult = (entry) => {
    setForm({
      name: entry.name || '',
      venue: entry.venue || '',
      city: [entry.city, entry.state].filter(Boolean).join(', '),
      start_date: entry.start_date || '',
      end_date: entry.end_date || '',
      // Kept so a created con can be traced back to the directory row it came
      // from; previously picking a result only prefilled free text.
      source_url: entry.source_url || entry.website || '',
    });
    setManualMode(true);
  };

  const isOwner = confirmDel && user && confirmDel.created_by === user.id;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-heading text-2xl font-extrabold">My Conventions</h1>
        <button onClick={() => setShowAdd(true)} className={BTN_PRIMARY}>
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : cons.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <CalendarDays className="w-10 h-10 mx-auto mb-3" />
          <p className="font-semibold">No conventions yet</p>
          <p className="text-sm mt-1 mb-4">Add a con, import its schedule, and start planning your weekend.</p>
          <button onClick={() => setShowAdd(true)} className={`${BTN_PRIMARY} mx-auto`}>
            <Plus className="w-4 h-4" /> Add your first con
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {cons.map((c) => (
            <div key={c.id} onClick={() => navigate(`/cons/${c.id}`)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/cons/${c.id}`); }}
              className="glass-card p-4 w-full text-left cursor-pointer">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading font-bold text-lg truncate">{c.name}</p>
                  <p className="text-sm flex items-center gap-1.5 mt-0.5">
                    <CalendarDays className="w-3.5 h-3.5" /> {fmtRange(c.start_date, c.end_date)}
                  </p>
                  {(c.venue || c.city) && (
                    <p className="text-sm flex items-center gap-1.5 mt-0.5">
                      <MapPin className="w-3.5 h-3.5" /> {[c.venue, c.city].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
                {countdown(c.start_date) && (
                  <span className="text-xs font-medium whitespace-nowrap shrink-0">
                    {countdown(c.start_date)}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-3 mt-3">
                <div className="flex items-center gap-4 text-xs min-w-0">
                  <span className="flex items-center gap-1 shrink-0">
                    <CalendarDays className="w-3.5 h-3.5" /> {c.my_event_count ?? 0} on your schedule
                  </span>
                  <span className="shrink-0">{c.event_count ?? 0} events imported</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmDel(c); }}
                  aria-label={`Remove ${c.name}`}
                  title="Remove convention"
                  className="-mr-1.5 -my-1 p-2.5 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={closeModal}>
          <div className="glass-card p-5 w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg">Add a convention</h2>
              <button onClick={closeModal} className="win-btn p-1.5">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!manualMode && (
              <div className="mb-4">
                <div className="flex gap-2 items-stretch">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 z-10 pointer-events-none" />
                    <input className={`${INPUT} pl-9`} placeholder="Search conventions…"
                      value={dirQuery} onChange={(e) => onDirQueryChange(e.target.value)} autoFocus />
                    {dirLoading && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin z-10" />
                    )}
                  </div>
                  <select
                    className={`${INPUT_BASE} w-24 shrink-0`}
                    value={dirState}
                    onChange={(e) => onDirStateChange(e.target.value)}
                    aria-label="Filter by state"
                  >
                    <option value="">All</option>
                    {dirStates.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="flex items-center justify-between gap-2 mt-2">
                  <p className="text-xs">
                    {dirView === 'list'
                      ? (dirTotal > 0
                          ? `${dirTotal} upcoming convention${dirTotal === 1 ? '' : 's'}${dirState ? ` in ${dirState}` : ''}`
                          : '')
                      : `${calEvents.length} in ${monthLabel(calMonth)}`}
                  </p>
                  <div className="flex gap-1 shrink-0" role="group" aria-label="View mode">
                    <button
                      onClick={() => setDirView('list')}
                      aria-pressed={dirView === 'list'}
                      className={`win-btn px-2 py-1 text-xs flex items-center gap-1 ${dirView === 'list' ? 'win-btn-default' : ''}`}
                    >
                      <List className="w-3.5 h-3.5" /> List
                    </button>
                    <button
                      onClick={() => setDirView('calendar')}
                      aria-pressed={dirView === 'calendar'}
                      className={`win-btn px-2 py-1 text-xs flex items-center gap-1 ${dirView === 'calendar' ? 'win-btn-default' : ''}`}
                    >
                      <CalendarDays className="w-3.5 h-3.5" /> Calendar
                    </button>
                  </div>
                </div>

                {dirView === 'calendar' ? (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-2">
                      <button
                        onClick={() => setCalMonth(shiftMonth(calMonth, -1))}
                        className="win-btn p-1.5"
                        aria-label="Previous month"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <p className="text-sm font-semibold">{monthLabel(calMonth)}</p>
                      <button
                        onClick={() => setCalMonth(shiftMonth(calMonth, 1))}
                        className="win-btn p-1.5"
                        aria-label="Next month"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>

                    {calError ? (
                      <div className="win-well p-4 text-center">
                        <p className="text-sm font-semibold">Couldn’t load the calendar</p>
                        <button
                          onClick={() => fetchCalendar(calMonth, dirState)}
                          className="win-btn text-xs mt-3 px-3 py-1.5"
                        >
                          Try again
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="win-raised p-2">
                          <div className="grid grid-cols-7 gap-1 mb-1">
                            {WEEKDAYS.map((d, i) => (
                              <div key={i} className="text-center text-[10px] font-semibold opacity-70 py-1">
                                {d}
                              </div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7 gap-1">
                            {monthGrid(calMonth).map((day, i) => {
                              if (!day) return <div key={i} />;
                              const dayEvents = dayMap[day] || [];
                              const count = dayEvents.length;
                              const isSelected = selectedDay === day;
                              const isToday = day === todayLocal();
                              return (
                                <button
                                  key={day}
                                  onClick={() => setSelectedDay(count ? (isSelected ? null : day) : null)}
                                  disabled={!count}
                                  aria-label={`${day}, ${count} convention${count === 1 ? '' : 's'}`}
                                  aria-pressed={isSelected}
                                  title={count ? dayEvents.map((e) => e.name).join('\n') : undefined}
                                  className={`relative aspect-square flex flex-col items-center justify-center text-xs rounded
                                    ${count ? 'win-btn cursor-pointer' : 'opacity-40 cursor-default'}
                                    ${isSelected ? 'win-btn-default font-bold' : ''}
                                    ${isToday && !isSelected ? 'underline underline-offset-2 font-semibold' : ''}`}
                                >
                                  <span>{Number(day.slice(-2))}</span>
                                  {count > 0 && (
                                    <span className="flex gap-0.5 mt-0.5" aria-hidden="true">
                                      {Array.from({ length: Math.min(count, 3) }).map((_, k) => (
                                        <span key={k} className="w-1 h-1 rounded-full bg-current opacity-80" />
                                      ))}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {calLoading && (
                          <div className="flex justify-center py-3">
                            <Loader2 className="w-4 h-4 animate-spin" />
                          </div>
                        )}

                        {selectedDay ? (
                          <div className="mt-2">
                            <p className="text-xs font-semibold mb-1">
                              {new Date(
                                Number(selectedDay.slice(0, 4)),
                                Number(selectedDay.slice(5, 7)) - 1,
                                Number(selectedDay.slice(8, 10))
                              ).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                            </p>
                            <div className="win-raised overflow-hidden max-h-48 overflow-y-auto">
                              {(dayMap[selectedDay] || []).map((entry) => (
                                <button key={entry.source_id} onClick={() => pickResult(entry)}
                                  className="w-full text-left px-4 py-3">
                                  <p className="text-sm font-semibold truncate">{entry.name}</p>
                                  <p className="text-xs mt-0.5">
                                    {[fmtRange(entry.start_date, entry.end_date), [entry.venue, entry.city, entry.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                                  </p>
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          !calLoading && (
                            <p className="text-xs mt-2 text-center">
                              {calEvents.length
                                ? 'Pick a highlighted day to see what’s on.'
                                : `Nothing listed in ${monthLabel(calMonth)}${dirState ? ` for ${dirState}` : ''}.`}
                            </p>
                          )
                        )}
                      </>
                    )}
                  </div>
                ) : dirError ? (
                  <div className="mt-2 win-well p-4 text-center">
                    <p className="text-sm font-semibold">Couldn’t load the directory</p>
                    <p className="text-xs mt-1">Check your connection, or add the con manually below.</p>
                    <button
                      onClick={() => fetchDirectory({ q: dirQuery, state: dirState })}
                      className="win-btn text-xs mt-3 px-3 py-1.5"
                    >
                      Try again
                    </button>
                  </div>
                ) : dirResults.length > 0 ? (
                  <>
                    <div className="mt-1.5 win-raised overflow-hidden max-h-72 overflow-y-auto">
                      {dirResults.map((entry) => (
                        <button key={entry.source_id} onClick={() => pickResult(entry)}
                          className="w-full text-left px-4 py-3">
                          <p className="text-sm font-semibold truncate">{entry.name}</p>
                          <p className="text-xs mt-0.5">
                            {[fmtRange(entry.start_date, entry.end_date), [entry.venue, entry.city, entry.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                          </p>
                        </button>
                      ))}
                    </div>
                    {dirResults.length < dirTotal && (
                      <button
                        onClick={loadMoreDirectory}
                        disabled={dirLoading}
                        className="win-btn text-xs mt-2 px-3 py-1.5 w-full justify-center"
                      >
                        {dirLoading ? 'Loading…' : `Show more (${dirTotal - dirResults.length} left)`}
                      </button>
                    )}
                  </>
                ) : !dirLoading ? (
                  <p className="text-xs mt-3 text-center">
                    {dirQuery.trim() || dirState
                      ? 'No matches — try a different search, or add it manually below.'
                      : 'No upcoming conventions listed yet — add yours manually below.'}
                  </p>
                ) : null}

                <button onClick={() => setManualMode(true)}
                  className="text-xs mt-3 underline underline-offset-2 w-full text-center">
                  Or add manually
                </button>
              </div>
            )}

            {manualMode && (
              <form onSubmit={create} className="space-y-3">
                <input className={INPUT} placeholder="Convention name *" value={form.name} onChange={set('name')} required autoFocus />
                <div className="grid grid-cols-2 gap-3">
                  <input className={INPUT} placeholder="Venue" value={form.venue} onChange={set('venue')} />
                  <input className={INPUT} placeholder="City" value={form.city} onChange={set('city')} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs">
                    Start
                    <input type="date" className={`${INPUT} mt-1`} value={form.start_date} onChange={set('start_date')} />
                  </label>
                  <label className="text-xs">
                    End
                    <input type="date" className={`${INPUT} mt-1`} value={form.end_date} onChange={set('end_date')} />
                  </label>
                </div>
                <div className="flex gap-2">
                  <button type="button"
                    onClick={() => { setManualMode(false); setForm({ name: '', venue: '', city: '', start_date: '', end_date: '' }); }}
                    className="flex items-center gap-1 px-3 py-2 win-btn text-sm">
                    ← Back
                  </button>
                  <button type="submit" disabled={busy} className={`${BTN_PRIMARY} flex-1 justify-center`}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
          onClick={() => !delBusy && setConfirmDel(null)}>
          <div className="glass-card p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 win-well flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h2 className="font-heading font-bold text-lg">
                  {isOwner ? 'Delete convention?' : 'Remove convention?'}
                </h2>
                <p className="text-sm mt-1">
                  {isOwner
                    ? <>This permanently deletes <span className="font-medium">{confirmDel.name}</span> and all its imported events, your schedule, packing list, and budget.</>
                    : <>This removes <span className="font-medium">{confirmDel.name}</span> from your conventions. You can rejoin it later.</>}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDel(null)} disabled={delBusy}
                className="flex-1 px-4 py-2.5 win-btn text-sm font-medium disabled:opacity-50">
                Cancel
              </button>
              <button onClick={remove} disabled={delBusy}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 win-btn win-btn-default text-sm font-semibold disabled:opacity-50">
                {delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {isOwner ? 'Delete' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
